<script setup lang="ts">
import { Dialog, Button } from 'primevue';
import { storeToRefs } from 'pinia';
import type { ProblemDetails } from 'shared/problems/domain/problemDetails';
import { useProductGroupStore } from 'shared/products/groups/stores/productGroupStore';
import { useLanguageStore } from 'shared/translations/stores/languageStore';
import { computed, ref } from 'vue';
import { useRefereshProductGroupStore } from '../commands/refreshProductGroupStore';
import { useDeleteProductGroup } from '../api/deleteProductGroup';
import { Assert } from 'shared/assert';
import ProblemDetailsView from 'shared/problems/components/ProblemDetailsView.vue';

defineExpose({
  show
});

const languageStore = useLanguageStore();
const { translations } = storeToRefs(languageStore);
const dialogTranslations = computed(() => translations.value.productGroups.deleteDialog);

const groupId = ref<string>();
const groupName = ref<string>('');
const visible = ref(false);
const loading = ref(false);
const deleteProductGroup = useDeleteProductGroup();
const problems = ref<ProblemDetails>();
const productGroupStore = useProductGroupStore();
const refreshProductGroupStore = useRefereshProductGroupStore();

async function show(id: string) {
  try {
    problems.value = undefined;
    loading.value = true;
    await refreshProductGroupStore.initialize();
    const group = productGroupStore.getById(id);
    Assert.definied(group);

    groupId.value = group.id;
    groupName.value = group.name;

    visible.value = true;
  } finally {
    loading.value = false;
  }
}

async function executeDelete() {
  const id = groupId.value;
  if (!id) throw new Error();
  try {
    loading.value = true;
    const response = await deleteProductGroup.execute({ groupId: id});

    if (response.ok) {
      groupId.value = undefined;
      visible.value = false;
    } else {
      problems.value = response.details;
    }

  } finally {
    loading.value = false;
  }
}

function onHide() {
  groupId.value = undefined;
  visible.value = false;
}

</script>

<template>
  <Dialog modal @hide="onHide" :header="dialogTranslations.header(groupName)" v-model:visible="visible">
    <ProblemDetailsView v-model:model-value="problems"></ProblemDetailsView>
    <div class="flex items-center gap-4">
      <i class="pi pi-exclamation-triangle !text-3xl" style="color: var(--p-red-400)"></i>
      <span>{{ dialogTranslations.description }}</span>
    </div>
    <template #footer>
      <Button :label="dialogTranslations.deleteButton" icon="pi pi-check" @click="executeDelete"></Button>
      <Button :label="translations.generic.cancel" icon="pi pi-times" text @click="onHide"></Button>
    </template>
  </Dialog>
</template>
